import { useState } from "react";
import { Link } from "react-router-dom";
import api from "../../utils/axiosInstance";
import { toast, confirmDialog } from "../Toast/toast";
import { btn } from "../Button/buttonStyles";
import LinkInvoicesModal from "./LinkInvoicesModal";
import { PAYMENT_PARTIES, COVERAGE_CLS, fmtNum, fmtDate, invoiceCoverage } from "./paymentParties";

const HEADERS = [
  "Code",
  "Linked payments",
  "Payment coverage",
  "Total after write off",
  "Amount covered by linked payments",
  "Amount covered by this payment",
  "Amount due",
  "Issued at",
  "",
];

// The payment's linked invoices, with linking and unlinking for those who may edit.
const LinkedInvoicesCard = ({ party, payment, currency, readOnly, onChange }) => {
  const config = PAYMENT_PARTIES[party];
  const [picking, setPicking] = useState(false);
  const [unlinking, setUnlinking] = useState(null);
  const links = (payment.invoiceLinks || []).filter((l) => l.invoice);

  const unlink = async (link) => {
    const code = link.invoice.invoice_code;
    if (!(await confirmDialog(`Unlink invoice ${code} from payment ${payment.payment_code}?`))) return;
    setUnlinking(link.id);
    try {
      const { data } = await api.put(
        `${config.endpoint}/${payment.id}/links`,
        { invoice_ids: links.filter((l) => l.id !== link.id).map((l) => l.invoice.id) },
        { withCredentials: true }
      );
      toast.success(`Invoice ${code} unlinked.`);
      onChange(data.data);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to unlink the invoice.");
    } finally {
      setUnlinking(null);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
        <span className="text-xl font-bold text-gray-900">Linked Invoices</span>
        {!readOnly && (
          <button onClick={() => setPicking(true)} className={btn("primary", "sm")}>
            Link invoices
          </button>
        )}
      </div>
      <p className="text-xs text-gray-500 px-5 pt-3">
        Total <span className="font-semibold">{links.length}</span> {links.length === 1 ? "item" : "items"}.
        {" "}Linked {fmtNum(payment.linked_amount)}, unlinked {fmtNum(payment.unlinked_amount)} {currency}.
      </p>
      <div className="overflow-x-auto px-5 pb-4 pt-2">
        <table className="w-full text-xs border-collapse min-w-[900px]">
          <thead>
            <tr className="border-b border-gray-200">
              {HEADERS.map((h) => (
                <th key={h} className="text-left font-semibold text-gray-700 py-2 pr-4 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {links.length === 0 ? (
              <tr>
                <td colSpan={HEADERS.length} className="py-3 text-xs text-gray-400">No linked invoices.</td>
              </tr>
            ) : (
              links.map((link) => {
                const inv = link.invoice;
                const coverage = invoiceCoverage(inv);
                const others = (inv.paymentLinks || []).map((l) => l.payment).filter(Boolean);
                return (
                  <tr key={link.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 pr-4 whitespace-nowrap">
                      <Link to={config.invoicePath(inv.id)} className="text-brand-600 hover:text-brand-800 underline font-medium">
                        {inv.invoice_code}
                      </Link>
                    </td>
                    <td className="py-2 pr-4">
                      <div className="flex flex-wrap gap-x-2">
                        {others.map((p) => (
                          <Link key={p.id} to={config.paymentPath(p.id)} className="text-brand-600 hover:underline font-medium">
                            {p.payment_code}
                          </Link>
                        ))}
                      </div>
                    </td>
                    <td className={`py-2 pr-4 whitespace-nowrap ${COVERAGE_CLS[coverage] ?? "text-gray-600"}`}>{coverage}</td>
                    <td className="py-2 pr-4 text-right">{fmtNum(Number(inv.total) - Number(inv.write_off_amount || 0))}</td>
                    <td className="py-2 pr-4 text-right">{fmtNum(inv.paid_amount)}</td>
                    <td className="py-2 pr-4 text-right font-semibold">{fmtNum(link.amount)}</td>
                    <td className="py-2 pr-4 text-right">{fmtNum(inv.due_amount)}</td>
                    <td className="py-2 pr-4 whitespace-nowrap text-gray-600">{fmtDate(inv.issued_at)}</td>
                    <td className="py-2 whitespace-nowrap">
                      {!readOnly && (
                        <button
                          onClick={() => unlink(link)}
                          disabled={unlinking !== null}
                          className={btn("danger", "sm")}
                        >
                          {unlinking === link.id ? "Unlinking..." : "Unlink"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {picking && (
        <LinkInvoicesModal
          party={party}
          payment={payment}
          currency={currency}
          onClose={() => setPicking(false)}
          onSaved={(updated) => {
            setPicking(false);
            onChange(updated);
          }}
        />
      )}
    </div>
  );
};

export default LinkedInvoicesCard;
